import React, { useEffect, useState } from 'react'

import { SxProps } from '@mui/material'

import * as EntityApi from 'redux/api/Entities'
import { useAiColumnIds, useEntity } from 'redux/hooks/Entities'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import SegmentVirtualizedTable from 'components_new/molecules/SegmentVirtualizedTable'

import { Sort } from 'utils/types'
import { getParsedData, ParsedData, sortParsedData } from 'utils/underlyingData'
import { useQuery, useParams, usePrevious } from 'utils/hooks'
import { useGlobalFilterForApi } from 'redux/hooks/GlobalFilter'

import Loading from './loading'
import { useTranslation } from 'language/translator'
import { useQueryParamState } from 'utils/queryHandler'

interface Props {
  sx?: SxProps
}

const getSort = (query: Record<string, string | null>): Sort => {
  const direction = query['direction']
  const attribute = query['attribute']

  return (direction === 'asc' || direction === 'desc') && attribute
    ? { attribute, direction }
    : { attribute: 'segment', direction: 'asc' }
}

const getQueryParams = (slices: string[], calculation: string | null) => {
  const params: EntityApi.IPreviewQueryParams = {}

  if (slices.length > 0) {
    params.slices = slices.join(',')
  }

  if (calculation) {
    params.calculation = calculation
  }

  return params
}

const EMPTY_SLICES: string[] = []

const EntityPreview = React.memo((props: Props) => {
  const { sx } = props
  const { id } = useParams<{ id: string }>()

  const { t } = useTranslation()
  const entity = useEntity(id)
  const aiColumnIds = useAiColumnIds(id)
  const globalFilter = useGlobalFilterForApi()
  const { query, setQuery } = useQuery()
  const [fetching, setFetching] = useState(true)
  const [data, setData] = useState<ParsedData>({
    rows: [],
    columns: []
  })

  const [slices] = useQueryParamState('slices', EMPTY_SLICES)
  const [calculation] = useQueryParamState<string | null>('calculation', null)

  const edit = Boolean(query['edit'])
  const prevEdit = usePrevious(edit)

  const sort = getSort(query)

  const fetchPreview = async () => {
    setFetching(true)

    try {
      const queryParams = getQueryParams(slices, calculation)

      const preview = await EntityApi.getPreview(id, queryParams, globalFilter)

      setData(
        sortParsedData(getParsedData(preview.data.data, aiColumnIds, t), sort)
      )
    } catch (e) {
      // do nothing
    } finally {
      setFetching(false)
    }
  }

  const handleUpdateSort = (newSort: Sort) => {
    setData(sortParsedData(data, newSort))
    setQuery(newSort)
  }

  useEffect(() => {
    // fetch on changes
    fetchPreview()
  }, [id, slices, calculation, globalFilter, entity?.terminology])

  useEffect(() => {
    // ended edit mode --> refetch preview
    if (prevEdit === true && edit === false) {
      fetchPreview()
    }
  }, [prevEdit])

  // success
  return (
    <Box
      sx={{
        borderRadius: 1,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        ...sx
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.76)',
          cursor: 'not-allowed',
          display: edit ? 'flex' : 'none',
          height: '100%',
          justifyContent: 'center',
          left: 0,
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 3
        }}
      >
        <Box sx={{ bgcolor: 'background.paper', p: 2 }}>
          <Text fontWeight="bold">{t('entities', 'exit_editing_to_view')}</Text>
        </Box>
      </Box>

      {fetching ? (
        <Loading />
      ) : (
        <>
          <SegmentVirtualizedTable
            columns={data.columns}
            data={data.rows}
            loading={fetching}
            sort={sort}
            onSort={handleUpdateSort}
            sx={{ flex: 1 }}
          />
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
              gap: 0.5,
              display: 'flex',
              px: 2,
              py: 1
            }}
          >
            <Icon color="disabled" fontSize="small" name="Info" />
            <Text color="text.secondary" variant="caption">
              {t('global', 'preview')}
            </Text>
          </Box>
        </>
      )}
    </Box>
  )
})

export default EntityPreview
